/**
 * RegenerateOffersDialog - Two-step secure offer regeneration
 *
 * Step 1 (params):  Pre-filled from lead data via API -> employee adjusts -> "Generate Preview"
 * Step 2 (preview): Employee reviews offers (OffersComparison) -> "Confirm & Update" or "Back"
 *
 * No lead edit access needed. All generation happens via broadcast API.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { leadBroadcastService, formatCurrency } from '../../../services/leadBroadcastService';
import { searchProductCompanies } from '../../../services/offerCalculationService';
import OffersComparison from '../../Offers/OffersComparison';
import PayslipOCRUpload from '../../OfferCalculation/PayslipOCRUpload';

const EMPLOYMENT_OPTIONS = [
    { label: 'Salaried', value: 'salaried' },
    { label: 'Self-Employed', value: 'self_employed' },
    { label: 'Business', value: 'business' },
    { label: 'Professional', value: 'professional' },
];

const RegenerateOffersDialog = ({ visible, onHide, broadcast, leadData, toast, onSuccess }) => {
    // Form state
    const [amount, setAmount] = useState(null);
    const [tenureMonths, setTenureMonths] = useState(null);
    const [salary, setSalary] = useState(null);
    const [cibilScore, setCibilScore] = useState(null);
    const [existingEmi, setExistingEmi] = useState(null);
    const [age, setAge] = useState(null);
    const [employmentType, setEmploymentType] = useState(null);

    // Company selection state
    const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [loanProductId, setLoanProductId] = useState(null);
    const [companySearchQuery, setCompanySearchQuery] = useState('');
    const selectedCompaniesRef = useRef([]);
    const companySearchTimeoutRef = useRef(null);

    // Lender (bank) selection — scoped regenerate. Only the chosen lenders'
    // offers in this broadcast will be replaced. Untouched lenders keep
    // whatever offers they currently have. Required: cannot Generate Preview
    // with zero lenders selected.
    const [selectedLenderIds, setSelectedLenderIds] = useState([]);
    // Lender options come from the backend's getOfferParams response.
    //
    // Why not derive from the `broadcast` prop: the Available Offers list
    // endpoint returns a slim toSummaryArray() row that does NOT include
    // broadcast_offers — so trying to read broadcast.broadcast_offers.offers
    // on the client always gave [] for collapsed-row Regenerate clicks.
    // The backend already loads the broadcast in getOfferParams; we just
    // ride along with that fetch and surface the unique lender list.
    const [lenderOptions, setLenderOptions] = useState([]);

    // Wizard state
    const [step, setStep] = useState('params');
    const [loadingParams, setLoadingParams] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [lastGeneration, setLastGeneration] = useState(null);

    // Payslip viewer state. We mirror what the lead wizard does (LeadWizard
    // line 5156): render PayslipOCRViewer with the saved OCR JSON +
    // signed-URL list. The viewer renders its own "View Payslip Data" button
    // that opens a comparison table + analytics dialog — same UX the wizard
    // shows after lead creation. We fetch the data once when the regen
    // dialog first opens (cheap — single endpoint call) and cache it for
    // the duration of the dialog.
    // documents_available lives on the EXPANDED lead data (fetched per-row
    // when the user expands a broadcast on the parent page), not on the
    // broadcast object itself. The parent forwards that cached payload via
    // the `leadData` prop. We still fall back to broadcast.lead /
    // broadcast.documents_available so the dialog works if some other caller
    // passes the documents_available there in the future.
    const docsAvailable = leadData?.documents_available
        ?? broadcast?.lead?.documents_available
        ?? broadcast?.documents_available
        ?? {};
    const hasPayslip = !!docsAvailable.has_payslip;

    const [payslipLoading, setPayslipLoading] = useState(false);
    const [payslipOcrData, setPayslipOcrData] = useState(null);
    const [payslipFiles, setPayslipFiles] = useState([]);
    const [payslipLoadError, setPayslipLoadError] = useState(null);

    // Per-lender NTH selections — keyed by lender_uuid → { selected_values,
    // percentages, nth_value, ... }. Drives lender-specific effective salary
    // in the offer-calc engine so e.g. AXIS can ignore Special Allowance
    // while Kotak counts it. Initial value comes from getOfferParams (the
    // wizard's last save / a previous regen's save). When the DSA adjusts
    // checkboxes inside PayslipOCRUpload and clicks its "Save Verification"
    // button, that component fires onFullOCRData with the fresh selections
    // — we capture them via handleFullOCRData below and send the latest
    // map when the DSA clicks Generate Preview.
    const [lenderNthSelections, setLenderNthSelections] = useState({});

    // Track whether we actually have payslips, based on the API response
    // rather than the documents_available hint alone. Parent-page caching
    // means the dialog can open BEFORE the lead has been expanded, in which
    // case `hasPayslip` is false even though the lead does have payslips.
    // We always attempt the fetch when the dialog opens — the backend
    // returns a clear "No payslip uploaded for this lead" message when
    // there's nothing to load, which we treat as "not available" silently.
    const [serverHasPayslip, setServerHasPayslip] = useState(false);

    // Fetch the payslip URLs + saved OCR JSON when the dialog opens.
    // Resets when the dialog closes so reopening for a different broadcast
    // re-fetches the right data.
    useEffect(() => {
        if (!visible || !broadcast?.id) {
            setPayslipOcrData(null);
            setPayslipFiles([]);
            setPayslipLoadError(null);
            setServerHasPayslip(false);
            return;
        }

        let cancelled = false;
        setPayslipLoading(true);
        setPayslipLoadError(null);

        leadBroadcastService.getBroadcastPayslipUrls(broadcast.id)
            .then((result) => {
                if (cancelled) return;
                if (result?.success) {
                    setPayslipFiles(Array.isArray(result.data) ? result.data : []);
                    setPayslipOcrData(result.ocr_payslip_details || null);
                    setServerHasPayslip(true);
                } else {
                    // "No payslip uploaded" is a legitimate state, not an
                    // error to surface — keep the viewer block hidden.
                    setServerHasPayslip(false);
                    if (result?.message && !/no payslip/i.test(result.message)) {
                        setPayslipLoadError(result.message);
                    }
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setServerHasPayslip(false);
                setPayslipLoadError(err?.message || 'Could not load payslip data');
            })
            .finally(() => {
                if (!cancelled) setPayslipLoading(false);
            });

        return () => { cancelled = true; };
    }, [visible, broadcast?.id]);

    // Show the viewer block whenever either the parent hint OR the actual
    // backend response says payslips exist.
    const showPayslipBlock = hasPayslip || serverHasPayslip;

    useEffect(() => {
        if (visible && broadcast) {
            // loadOfferParams now also seeds lenderOptions + pre-selects them
            // all, so we don't need a separate broadcast-prop-based seed
            // (which never worked because the list endpoint doesn't carry
            // broadcast_offers).
            loadOfferParams();
        }
        if (!visible) {
            setStep('params');
            setPreviewData(null);
            setPreviewing(false);
            setConfirming(false);
            setCompanies([]);
            selectedCompaniesRef.current = [];
            setCompanySearchQuery('');
            setSelectedLenderIds([]);
            setLenderOptions([]);
            // Drop locally-edited NTH selections when the dialog closes —
            // they'll be reloaded from the lead row the next time it opens.
            setLenderNthSelections({});
        }
    }, [visible, broadcast]);  // eslint-disable-line react-hooks/exhaustive-deps

    const loadOfferParams = async () => {
        setLoadingParams(true);
        try {
            const result = await leadBroadcastService.getOfferParams(broadcast.id);
            if (result.success && result.data?.params) {
                const p = result.data.params;
                if (p.amount) setAmount(p.amount);
                if (p.tenure_months) setTenureMonths(p.tenure_months);
                if (p.salary) setSalary(p.salary);
                if (p.cibil_score) setCibilScore(p.cibil_score);
                if (p.existing_emi) setExistingEmi(p.existing_emi);
                if (p.age) setAge(p.age);
                if (p.employment_type) setEmploymentType(p.employment_type);
                setLastGeneration(result.data.last_generation);
                setLoanProductId(result.data.loan_product_id);

                const preselected = result.data.selected_companies || [];
                if (preselected.length > 0) {
                    selectedCompaniesRef.current = preselected;
                    setSelectedCompanyIds(preselected.map(c => c.value));
                }

                // Pre-fill per-lender NTH selections from whatever the wizard
                // last saved on the lead. Shape: lender_uuid → {selected_values,
                // percentages, nth_value, ...}. Falls through to {} when the
                // lead has no per-lender overrides yet.
                if (result.data.lender_nth_selections && typeof result.data.lender_nth_selections === 'object') {
                    setLenderNthSelections(result.data.lender_nth_selections);
                }

                // Lender options + pre-selection. Server returns the unique
                // list of lenders currently in the broadcast. We pre-tick
                // them all — the typical "I want to refresh everything"
                // workflow — and the user unticks the ones they want to
                // leave frozen. Empty list → multiselect shows the friendly
                // "no lenders" warning + Generate Preview stays disabled.
                if (Array.isArray(result.data.current_lenders)) {
                    setLenderOptions(result.data.current_lenders);
                    setSelectedLenderIds(result.data.current_lenders.map((l) => l.value));
                }
            }
        } catch (error) {
            const offers = broadcast.broadcast_offers?.offers || broadcast.offers || [];
            if (offers.length > 0) {
                const first = offers[0];
                setAmount(first.loan_amount || first.Loan_Amount || null);
                setTenureMonths(first.tenure || first.tenure_months || first.Tenure || null);
            }
        } finally {
            setLoadingParams(false);
        }
    };

    const searchCompanies = useCallback((query) => {
        if (!loanProductId) return;
        if (companySearchTimeoutRef.current) clearTimeout(companySearchTimeoutRef.current);
        setCompanySearchQuery(query || '');
        if (!query || query.length < 3) { setCompanies([]); setLoadingCompanies(false); return; }

        companySearchTimeoutRef.current = setTimeout(async () => {
            setLoadingCompanies(true);
            try {
                const response = await searchProductCompanies(loanProductId, query, 30);
                if (response.success) {
                    const data = Array.isArray(response.data) ? response.data : (response.data?.companies || response.data || []);
                    setCompanies(data.map(c => ({ label: c.company_name || c.name || c.label, value: c.id, category: c.category, lender_name: c.lender_name })));
                }
            } catch (error) { setCompanies([]); } finally { setLoadingCompanies(false); }
        }, 300);
    }, [loanProductId]);

    const buildOfferParams = () => {
        const params = { amount, tenure_months: tenureMonths };
        if (salary) params.salary = salary;
        if (cibilScore) params.cibil_score = cibilScore;
        if (existingEmi) params.existing_emi = existingEmi;
        if (age) params.age = age;
        if (employmentType) params.employment_type = employmentType;
        if (selectedCompanyIds.length > 0) params.selected_company_ids = selectedCompanyIds;
        // Lender scoping for partial regenerate. Backend uses this to
        // (a) filter the engine output to only these lenders, and
        // (b) restrict the merge so untouched lenders' offers stay in
        //     the broadcast unchanged.
        if (selectedLenderIds.length > 0) {
            params.selected_lender_ids = selectedLenderIds;
        }
        // Per-lender NTH selections. Always send when non-empty so the offer
        // engine uses lender-specific effective salaries. The backend
        // falls back to lead.lender_nth_selections if the array is empty,
        // so omitting it here is also safe (existing leads keep working).
        if (lenderNthSelections && Object.keys(lenderNthSelections).length > 0) {
            params.lender_nth_selections = lenderNthSelections;
        }
        return params;
    };

    const handlePreview = async () => {
        // Loan Amount + Tenure are OPTIONAL by design now. When both are blank,
        // the engine runs in discovery mode and returns ranges instead of
        // exact EMI / loan-amount-eligible values. Useful when the DSA only
        // wants to refresh per-lender salary calculations without committing
        // to a specific loan amount yet.
        //
        // Lender selection IS required — the partial-replace flow needs to
        // know which lenders' offers to refresh. Without a lender pick the
        // call would silently replace ALL unclaimed offers, which is exactly
        // what we DON'T want now that this dialog is "scoped regenerate".
        if (selectedLenderIds.length === 0) {
            toast?.current?.show({
                severity: 'warn',
                summary: 'Select lenders',
                detail: 'Pick at least one lender to regenerate offers for. Other lenders\' offers will stay frozen.',
                life: 4000,
            });
            return;
        }
        setPreviewing(true);
        try {
            const result = await leadBroadcastService.previewRegenerateOffers(broadcast.id, buildOfferParams());
            if (result.success) { setPreviewData(result.data); setStep('preview'); }
            else toast?.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to generate preview', life: 4000 });
        } catch (error) {
            toast?.current?.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to generate preview.', life: 4000 });
        } finally { setPreviewing(false); }
    };

    const handleConfirm = async () => {
        setConfirming(true);
        try {
            const result = await leadBroadcastService.regenerateOffers(broadcast.id, { offerParams: buildOfferParams() });
            if (result.success) {
                const stats = result.data?.stats;
                toast?.current?.show({ severity: 'success', summary: 'Broadcast Updated', detail: result.message || `${stats?.added || 0} new offers added, ${stats?.removed || 0} replaced`, life: 5000 });
                onHide();
                onSuccess?.();
            } else toast?.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to update broadcast', life: 4000 });
        } catch (error) {
            toast?.current?.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to update broadcast.', life: 4000 });
        } finally { setConfirming(false); }
    };

    // Merged options: pre-selected + search results
    const companyOptions = (() => {
        const all = [...(selectedCompaniesRef.current || []), ...companies];
        return Array.from(new Map(all.map(c => [c.value, c])).values());
    })();

    const renderParamsStep = () => {
        if (loadingParams) {
            return (
                <div className="flex justify-content-center align-items-center py-6">
                    <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                    <span className="ml-3 text-600">Loading previous parameters...</span>
                </div>
            );
        }

        return (
            <>
                <Message severity="info" className="w-full mb-4" text="Parameters are pre-filled from the last generation. Adjust as needed and preview new offers." />

                {lastGeneration && (
                    <div className="surface-100 border-round p-3 mb-3 flex align-items-center gap-2">
                        <i className="pi pi-history text-500" />
                        <span className="text-600 text-sm">
                            Last generated: {new Date(lastGeneration.generated_at).toLocaleDateString('en-IN')} —
                            {' '}{lastGeneration.offers_count} offers
                            {lastGeneration.best_offer_rate && `, best rate ${lastGeneration.best_offer_rate}%`}
                        </span>
                    </div>
                )}

                {/* Inline payslip editor — same component the lead wizard
                    edit page uses (with hideUpload=true so we don't allow
                    re-uploading from inside the regen dialog). DSAs can:
                      - View the OCR'd payslip data per month side-by-side
                      - Switch between bank-wise tabs (Default / AXIS / Kotak)
                      - Toggle which earnings/deductions each lender counts
                      - Save verification → fires onFullOCRData with the new
                        lender_nth_selections map, which we capture and send
                        with Generate Preview.
                    onUpload/onOCRComplete are no-ops because hideUpload=true
                    means there's no file picker. onApplyToForm is null
                    because the regen flow doesn't write to a wizard form. */}
                {showPayslipBlock && (
                    <div className="mb-3">
                        {payslipLoading ? (
                            <div className="surface-100 border-round p-3 flex align-items-center gap-2 text-600 text-sm">
                                <ProgressSpinner style={{ width: '18px', height: '18px' }} strokeWidth="4" />
                                Loading payslip data…
                            </div>
                        ) : payslipLoadError ? (
                            <Message severity="warn" text={payslipLoadError} className="w-full" />
                        ) : payslipOcrData ? (
                            <>
                                <Message
                                    severity="info"
                                    text="Select earnings & deductions per lender, then click Save Verification before Generate Preview. Each lender's selections drive its own effective salary."
                                    className="w-full mb-2 text-xs"
                                />
                                <PayslipOCRUpload
                                    documentDefinition={{ id: 'payslip-ocr-regen', name: 'Payslip / Salary Slip', code: 'payslip', icon: 'pi pi-file-pdf', max_file_size_mb: 5, max_files: 3, allowed_formats: ['pdf', 'jpg', 'jpeg', 'png'], category: 'income_proof' }}
                                    loanProductId={loanProductId}
                                    uploadedFiles={payslipFiles}
                                    isUploading={false}
                                    uploadProgress={0}
                                    onUpload={() => {}}
                                    onOCRComplete={() => {}}
                                    onFullOCRData={(fullData) => {
                                        // Capture the freshly saved per-lender NTH map so
                                        // Generate Preview / Confirm can include it in the
                                        // regen payload. Other fields in fullData are
                                        // ignored — we don't write back to the lead row
                                        // from the dialog (the backend persists on submit).
                                        if (fullData?.lender_nth_selections) {
                                            setLenderNthSelections(fullData.lender_nth_selections);
                                        }
                                    }}
                                    onRemove={() => {}}
                                    toast={toast}
                                    // Merge the canonical NTH column value INTO the OCR
                                    // JSON before handing it to the upload component. The
                                    // upload component reads lender_nth_selections off
                                    // savedOcrData; the lead row holds the freshest copy
                                    // in its own column (kept in sync by the wizard and
                                    // by this dialog's submit path). Without the merge,
                                    // re-opening this dialog after a wizard NTH save
                                    // could show stale tabs.
                                    savedOcrData={
                                        lenderNthSelections && Object.keys(lenderNthSelections).length > 0
                                            ? { ...payslipOcrData, lender_nth_selections: lenderNthSelections }
                                            : payslipOcrData
                                    }
                                    hideUpload={true}
                                    leadId={null}
                                />
                            </>
                        ) : (
                            <Message
                                severity="info"
                                text="Payslip is uploaded but OCR data isn't saved for this lead — open the lead to re-run OCR."
                                className="w-full"
                            />
                        )}
                    </div>
                )}

                <div className="formgrid grid">
                    {/* Lender selection — required. Drives which lenders' offers
                        get replaced. All other lenders stay frozen in the broadcast.
                        Pre-populated with every lender currently in the broadcast
                        so the default is "regenerate everything"; user unticks
                        to scope down. */}
                    <div className="field col-12">
                        <label htmlFor="regen-lenders" className="font-semibold block mb-2">
                            Replace offers for these lenders
                            <span className="text-red-500 ml-1">*</span>
                            {selectedLenderIds.length > 0 && lenderOptions.length > 0 && (
                                <Tag
                                    value={`${selectedLenderIds.length} of ${lenderOptions.length} selected`}
                                    severity={selectedLenderIds.length === lenderOptions.length ? 'success' : 'warning'}
                                    className="ml-2 text-xs"
                                />
                            )}
                        </label>
                        {lenderOptions.length === 0 ? (
                            <Message
                                severity="warn"
                                text="Couldn't load the lender list. Close and reopen the dialog, or refresh the page if the problem persists."
                                className="w-full"
                            />
                        ) : (
                            <>
                                <MultiSelect
                                    id="regen-lenders"
                                    value={selectedLenderIds}
                                    options={lenderOptions}
                                    onChange={(e) => setSelectedLenderIds(e.value || [])}
                                    optionLabel="label"
                                    optionValue="value"
                                    className="w-full"
                                    placeholder="Pick lenders to regenerate"
                                    display="chip"
                                    maxSelectedLabels={5}
                                />
                                <small className="text-500 mt-1 block">
                                    Only the selected lenders' offers will be replaced.
                                    Unticked lenders keep their current offers (including any claimed ones).
                                </small>
                            </>
                        )}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="regen-amount" className="font-semibold block mb-2">
                            Loan Amount
                            <span className="text-500 text-xs font-normal ml-2">(optional)</span>
                        </label>
                        <InputNumber id="regen-amount" value={amount} onValueChange={(e) => setAmount(e.value)} mode="currency" currency="INR" locale="en-IN" minFractionDigits={0} maxFractionDigits={0} min={10000} className="w-full" placeholder="Leave blank for ranges" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="regen-tenure" className="font-semibold block mb-2">
                            Tenure (months)
                            <span className="text-500 text-xs font-normal ml-2">(optional)</span>
                        </label>
                        <InputNumber id="regen-tenure" value={tenureMonths} onValueChange={(e) => setTenureMonths(e.value)} min={3} max={360} className="w-full" placeholder="Leave blank for ranges" suffix=" months" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="regen-salary" className="font-semibold block mb-2">Net Monthly Salary</label>
                        <InputNumber id="regen-salary" value={salary} onValueChange={(e) => setSalary(e.value)} mode="currency" currency="INR" locale="en-IN" minFractionDigits={0} maxFractionDigits={0} className="w-full" placeholder="e.g. 50,000" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="regen-cibil" className="font-semibold block mb-2">CIBIL Score</label>
                        <InputNumber id="regen-cibil" value={cibilScore} onValueChange={(e) => setCibilScore(e.value)} min={300} max={900} className="w-full" placeholder="e.g. 750" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="regen-emi" className="font-semibold block mb-2">Existing EMI</label>
                        <InputNumber id="regen-emi" value={existingEmi} onValueChange={(e) => setExistingEmi(e.value)} mode="currency" currency="INR" locale="en-IN" minFractionDigits={0} maxFractionDigits={0} className="w-full" placeholder="e.g. 10,000" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="regen-age" className="font-semibold block mb-2">Age</label>
                        <InputNumber id="regen-age" value={age} onValueChange={(e) => setAge(e.value)} min={18} max={70} className="w-full" placeholder="e.g. 30" suffix=" years" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="regen-employment" className="font-semibold block mb-2">Employment Type</label>
                        <Dropdown id="regen-employment" value={employmentType} options={EMPLOYMENT_OPTIONS} onChange={(e) => setEmploymentType(e.value)} className="w-full" placeholder="Select type" showClear />
                    </div>

                    {loanProductId && (
                        <div className="field col-12">
                            <label htmlFor="regen-companies" className="font-semibold block mb-2">
                                Employer / Company
                                {selectedCompanyIds.length > 0 && (
                                    <Tag value={`${selectedCompanyIds.length} selected`} severity="info" className="ml-2 text-xs" />
                                )}
                            </label>
                            <MultiSelect
                                id="regen-companies"
                                value={selectedCompanyIds}
                                options={companyOptions}
                                onChange={(e) => {
                                    setSelectedCompanyIds(e.value || []);
                                    const allOpts = [...(selectedCompaniesRef.current || []), ...companies];
                                    selectedCompaniesRef.current = allOpts.filter(c => e.value?.includes(c.value));
                                }}
                                optionLabel="label"
                                optionValue="value"
                                filter
                                filterPlaceholder="Type 3+ characters to search..."
                                onFilter={(e) => searchCompanies(e.filter)}
                                className="w-full"
                                placeholder="Select Employer / Company"
                                emptyFilterMessage={companySearchQuery.length < 3 ? 'Enter at least 3 characters to search' : loadingCompanies ? 'Searching...' : 'No companies found'}
                                emptyMessage="Type to search companies"
                                display="chip"
                                maxSelectedLabels={3}
                                showClear
                                loading={loadingCompanies}
                                itemTemplate={(option) => (
                                    <div className="flex flex-column">
                                        <span className="font-medium">{option.label}</span>
                                        {option.category && <small className="text-500">{option.category}</small>}
                                    </div>
                                )}
                            />
                            <small className="text-500 mt-1 block">Search and select companies to filter offers. Leave empty to use all.</small>
                        </div>
                    )}
                </div>

                <p className="text-sm text-600 mt-3 mb-0">
                    <i className="pi pi-info-circle mr-1" />
                    Only <strong>lender selection</strong> is required. Loan amount and tenure are
                    optional — leave blank to get range-based discovery offers. Per-lender salary
                    is driven by the Payslip OCR editor above (different lenders can count different
                    earnings).
                </p>
            </>
        );
    };

    const renderPreviewStep = () => {
        if (!previewData) return null;
        const { offers, total_offers, best_rate, best_emi, merge_preview } = previewData;

        return (
            <>
                <div className="surface-100 border-round p-3 mb-3">
                    <div className="flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-check-circle text-green-500 text-xl" />
                            <span className="font-semibold text-900">{total_offers} offers generated</span>
                        </div>
                        <div className="flex gap-3">
                            {best_rate && <div className="text-center"><div className="text-xs text-500">Best Rate</div><div className="font-bold text-green-600">{best_rate}%</div></div>}
                            {best_emi && <div className="text-center"><div className="text-xs text-500">Best EMI</div><div className="font-bold text-primary">{formatCurrency(best_emi)}</div></div>}
                        </div>
                    </div>
                </div>

                {merge_preview && (
                    <div className="flex flex-wrap gap-2 mb-3">
                        <Tag icon="pi pi-shield" severity="info" value={`${merge_preview.protected} protected`} className="text-xs" />
                        <Tag icon="pi pi-trash" severity="warning" value={`${merge_preview.will_be_removed} will be replaced`} className="text-xs" />
                        {merge_preview.frozen_by_lender_scope > 0 && (
                            <Tag
                                icon="pi pi-lock"
                                severity="secondary"
                                value={`${merge_preview.frozen_by_lender_scope} frozen (out-of-scope lenders)`}
                                className="text-xs"
                                tooltip="These offers are kept untouched because their lender wasn't selected for regeneration"
                                tooltipOptions={{ position: 'top' }}
                            />
                        )}
                        {merge_preview.truly_new > 0 && <Tag icon="pi pi-plus" severity="success" value={`${merge_preview.truly_new} new`} className="text-xs" />}
                        {merge_preview.content_changed > 0 && <Tag icon="pi pi-sync" severity="warning" value={`${merge_preview.content_changed} rates updated`} className="text-xs" />}
                        {merge_preview.identical > 0 && <Tag icon="pi pi-check" className="text-xs" value={`${merge_preview.identical} unchanged`} />}
                    </div>
                )}

                <OffersComparison offers={offers} selectedOffer={null} onSelectOffer={() => {}} readOnly={true} />
            </>
        );
    };

    return (
        <Dialog
            header={
                <div className="flex align-items-center gap-2">
                    <i className={`pi ${step === 'params' ? 'pi-refresh' : 'pi-eye'} text-primary`} />
                    <span>{step === 'params' ? 'Regenerate Offers' : `Preview — ${previewData?.total_offers || 0} Offers`}</span>
                </div>
            }
            visible={visible}
            onHide={onHide}
            style={{ width: '90vw', maxWidth: step === 'params' ? '550px' : '1200px' }}
            breakpoints={{ '768px': '95vw' }}
            modal
            footer={
                step === 'params' ? (
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={onHide} disabled={previewing} />
                        <Button
                            label="Generate Preview"
                            icon="pi pi-eye"
                            className="p-button-warning"
                            onClick={handlePreview}
                            loading={previewing}
                            disabled={selectedLenderIds.length === 0 || loadingParams}
                            tooltip={selectedLenderIds.length === 0 ? 'Select at least one lender to regenerate offers for' : undefined}
                            tooltipOptions={{ position: 'top' }}
                        />
                    </div>
                ) : (
                    <div className="flex justify-content-between w-full">
                        <Button label="Back" icon="pi pi-arrow-left" className="p-button-text" onClick={() => setStep('params')} disabled={confirming} />
                        <div className="flex gap-2">
                            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={onHide} disabled={confirming} />
                            <Button label="Confirm & Update Broadcast" icon="pi pi-check" className="p-button-success" onClick={handleConfirm} loading={confirming} />
                        </div>
                    </div>
                )
            }
        >
            {step === 'params' ? renderParamsStep() : renderPreviewStep()}
        </Dialog>
    );
};

export default RegenerateOffersDialog;
